<template>
  <div ref="dataTableTitle" class="table-title">
    <v-container fluid>
      <v-row class="mt-1" align="center" align-content="center">
        <v-col :cols="colsHeaders[0]">
          <v-select
            v-model="weatherRating"
            :items="weatherRatings"
            class="weather-field"
            :label="t('views.dashboard.weatherLabel')"
            item-title="name"
            item-value="value"
            variant="outlined"
            density="compact"
            hide-details />
        </v-col>
        <v-col :cols="colsHeaders[0]">
          <v-select
            v-model="buffer"
            :items="buffers"
            class="weather-field"
            :label="t('views.dashboard.bufferLabel')"
            item-title="name"
            item-value="value"
            variant="outlined"
            density="compact"
            hide-details />
        </v-col>
        <v-col class="flex-grow-1 d-flex align-center" :cols="colsHeaders[1]">
          <FeatureSearchBar></FeatureSearchBar>
        </v-col>

        <v-col class="d-flex flex-row justify-end align-center" :cols="colsHeaders[2]">
          <!-- DAYS buttons -->
          <v-btn-toggle v-model="days" style="border-radius: 10px">
            <v-btn active-class="accent--text" class="daysButtons" color="primary" :value="3">3 {{ t('common.time.days') }}</v-btn>
            <v-btn active-class="accent--text" class="daysButtons" color="primary" :value="5">5 {{ t('common.time.days') }}</v-btn>
            <v-btn active-class="accent--text" class="daysButtons" color="primary" :value="7">7 {{ t('common.time.days') }}</v-btn>
          </v-btn-toggle>

          <!-- Export button-->
          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <v-btn variant="text" v-bind="tooltipProps" color="primary" @click="openDialog = true">
                <v-icon size="30">mdi-file-export</v-icon>
              </v-btn>
            </template>
            {{ t('common.actions.export') }}
          </v-tooltip>

          <!-- TableHeaders selector button -->
          <ZDataTableHeadersSelector v-model="visibleHeaders" :headers="headers">
            <template #default="{ menuProps }">
              <v-tooltip location="top">
                <template #activator="{ props: tooltipProps }">
                  <v-btn variant="text" v-bind="{ ...menuProps, ...tooltipProps }" color="primary">
                    <v-icon size="30">mdi-table-eye</v-icon>
                  </v-btn>
                </template>
                {{ t('views.dashboard.columns') }}
              </v-tooltip>
            </template>
          </ZDataTableHeadersSelector>

          <!-- Filter button-->
          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <v-btn variant="text" v-bind="tooltipProps" color="primary" @click="goToFilter">
                <v-icon size="30">mdi-tune</v-icon>
              </v-btn>
            </template>
            {{ t('common.concepts.filters') }}
          </v-tooltip>

          <!-- Reload button-->
          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <v-btn variant="text" v-bind="tooltipProps" color="primary" @click="reload">
                <v-icon size="30">mdi-reload</v-icon>
              </v-btn>
            </template>
            {{ t('common.actions.reload') }}
          </v-tooltip>

          <!-- Close button-->
          <v-tooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <v-btn variant="text" v-bind="tooltipProps" color="primary" @click="close">
                <v-icon size="30">mdi-close</v-icon>
              </v-btn>
            </template>
            {{ t('common.actions.close') }}
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>
    <v-dialog v-model="openDialog" class="w-50">
      <ExportDialog :visible-headers="visibleHeaders" @close-dialog="openDialog = false" />
    </v-dialog>
  </div>
</template>

<script lang="ts" setup>
// framework imports
import type { PropType, Ref } from 'vue'
import { computed, ref, watch } from 'vue'

import { useRouter } from 'vue-router'

import { storeToRefs } from 'pinia'

import { useResizeObserver, useVModel } from '@vueuse/core'
import { debounce } from 'lodash'
import { useI18n } from 'vue-i18n'

import type { Header } from '@tsyl/zapan2/lib/types/ZDataTable'

import ExportDialog from '@/components/Dashboard/ExportDialog.vue'
import FeatureSearchBar from '@/components/Dashboard/FeatureSearchBar.vue'
import FiltersStore from '@/stores/FiltersStore.ts'

const { t } = useI18n()
const props = defineProps({
  isExpanded: {
    type: Boolean,
    default: false
  },
  visibleHeaders: {
    type: Array as PropType<string[]>,
    default: undefined
  },
  headers: {
    type: Array as PropType<Header[]>,
    default: null
  }
})

const emits = defineEmits(['update:visibleHeaders', 'update:table', 'weather', 'days', 'buffer', 'reload'])

const router = useRouter()
const { filtersOpen } = storeToRefs(FiltersStore())

const openDialog = ref(false)

const weatherRatings = computed(() => [
  {
    name: t('domain.fields.sfdi'),
    value: 'SFDI'
  },
  {
    name: t('views.dashboard.erc'),
    value: 'ERC'
  },
  {
    name: t('views.dashboard.bi'),
    value: 'BI'
  }
])
const weatherRating = ref('ERC')
const buffers = computed(() =>
  ['1', '5', '10'].map((size) => ({ name: `${t('domain.fields.buffer')} ${size}`, value: `valueBuffer${size}` }))
)
const buffer = ref('valueBuffer1')
const days = ref(7)

// The parent holds `undefined` until the headers selector fills it in; the children type it as string[].
const visibleHeaders = useVModel(props, 'visibleHeaders', emits) as Ref<string[]>

// resize hook
const dataTableTitle = ref()
const colsHeaders = ref(['4', 'auto', 'auto'])

useResizeObserver(
  dataTableTitle,
  debounce((entries) => {
    const entry = entries[0]
    const { width } = entry.contentRect
    if (width > 1588) {
      colsHeaders.value = ['auto', 'auto', 'auto']
    } else if (width > 1234) {
      colsHeaders.value = ['4', 'auto', 'auto']
    } else if (width > 1110) {
      colsHeaders.value = ['3', 'auto', 'auto']
    } else if (width > 670) {
      colsHeaders.value = ['12', 'auto', 'auto']
    } else {
      colsHeaders.value = ['12', '12', '12']
    }
  }, 100)
)

function goToFilter() {
  filtersOpen.value = !filtersOpen.value
}

function reload() {
  emits('reload')
}

function close() {
  router.push({ path: '/' })
}

watch(
  () => weatherRating.value,
  (val) => {
    emits('weather', val)
  }
)
watch(
  () => days.value,
  (val) => {
    emits('days', val)
  }
)
watch(
  () => buffer.value,
  (val) => {
    emits('buffer', val)
  }
)
</script>

<style lang="scss" scoped>
.table-title {
  background-color: rgb(var(--v-theme-on-surface-variant));
  border-radius: 10px 10px 0px 0px;
}

.daysButtons {
  text-transform: capitalize;
  font-family: sans-serif;
  letter-spacing: 0px;
  font-weight: bold;
}

:deep(.weather-field .v-field__outline) {
  color: rgb(var(--v-theme-primary));
  border-radius: 10px;
}
</style>
